import { Flex } from 'antd';
import { ReactNode, MouseEventHandler, forwardRef, KeyboardEvent } from 'react';

interface FloatingToolbarActionsProps {
  icon: ReactNode;
  label?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  iconOnly?: boolean; // force icon only
  danger?: boolean;
  tabIndex?: number;
  'aria-label'?: string;
}

/**
 * Floating toolbar action item.
 * - Shows icon + label on desktop
 * - Shows icon only on mobile (≤640px)
 * - Supports danger styling
 */
const FloatingToolbarActions = forwardRef<
  HTMLDivElement,
  FloatingToolbarActionsProps
>(
  (
    {
      icon,
      label = '',
      onClick,
      iconOnly = false,
      danger = false,
      tabIndex,
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
      }
    };

    return (
      <Flex
        ref={ref}
        align='center'
        className='toolbar-action'
        onClick={onClick}
        onKeyDown={handleKeyDown}
        style={{ cursor: 'pointer' }}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        role='button'
      >
        {icon}
        {/* Label: hidden on mobile via SCSS */}
        {!iconOnly && label && (
          <span className={`toolbar-label ${danger ? 'danger' : ''}`}>
            {label}
          </span>
        )}
      </Flex>
    );
  }
);

FloatingToolbarActions.displayName = 'FloatingToolbarActions';

export default FloatingToolbarActions;
